import { useCallback, useMemo, useState } from 'react';
import { Post } from '../model/post.types';
import { useDeletePost } from '../api/useDeletePost';
import { usePinPostAction } from '../lib/usePinPostAction';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';

type Props = {
  post: Post;
  pinnedCount: number;
  onEdit?: () => void;
};

export const OwnPostActionsMenu = ({ post, pinnedCount, onEdit }: Props) => {
  const { communityId } = useCommunityContext();
  const deletePost = useDeletePost();
  const pin = usePinPostAction(post, communityId, pinnedCount);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const openDelete = useCallback(() => setConfirmingDelete(true), []);
  const dismiss = useCallback(() => setConfirmingDelete(false), []);
  const confirmDelete = useCallback(() => {
    analytics.trackPostDeleted(post);
    deletePost({ communityId, postId: post.id });
    setConfirmingDelete(false);
  }, [deletePost, communityId, post]);

  const items = useMemo<ActionMenuItem[]>(() => {
    const entries: Array<ActionMenuItem | null> = [
      {
        key: 'pin',
        label: 'Pin to top',
        disabled: pin.disabled,
        onClick: pin.toggle,
        toggle: { checked: pin.checked, helperText: pin.helperText },
      },
      onEdit ? { key: 'edit', label: 'Edit post', onClick: onEdit } : null,
      {
        key: 'delete',
        label: 'Delete post',
        destructive: true,
        onClick: openDelete,
      },
    ];
    return entries.filter((item): item is ActionMenuItem => item !== null);
  }, [
    pin.disabled,
    pin.toggle,
    pin.checked,
    pin.helperText,
    onEdit,
    openDelete,
  ]);

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirmingDelete}
        title="Are you sure you want to delete this post?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
    </>
  );
};
